import { useState, useCallback, useMemo } from 'react';
import { Network, Copy, Check } from 'lucide-react';

interface SubnetInfo {
  cidr: string;
  networkAddress: string;
  broadcastAddress: string;
  firstUsable: string;
  lastUsable: string;
  subnetMask: string;
  wildcardMask: string;
  totalHosts: number;
  usableHosts: number;
  prefix: number;
  ipClass: string;
  ipType: string;
  binaryNetwork: string;
  binaryMask: string;
}

// Convert IP to 32-bit integer
function ipToInt(ip: string): number {
  const parts = ip.split('.').map(Number);
  return ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
}

// Convert 32-bit integer to IP
function intToIp(int: number): string {
  return [
    (int >>> 24) & 255,
    (int >>> 16) & 255,
    (int >>> 8) & 255,
    int & 255,
  ].join('.');
}

// Convert IP to binary string
function ipToBinary(ip: string): string {
  return ip
    .split('.')
    .map((octet) => parseInt(octet, 10).toString(2).padStart(8, '0'))
    .join('.');
}

// Validate IP address
function isValidIp(ip: string): boolean {
  const parts = ip.split('.');
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    const num = parseInt(part, 10);
    return !isNaN(num) && num >= 0 && num <= 255 && part === String(num);
  });
}

// Get IP class
function getIpClass(firstOctet: number): string {
  if (firstOctet < 128) return 'A';
  if (firstOctet < 192) return 'B';
  if (firstOctet < 224) return 'C';
  if (firstOctet < 240) return 'D (Multicast)';
  return 'E (Reserved)';
}

// Get IP type
function getIpType(ip: string): string {
  const parts = ip.split('.').map(Number);
  const [a, b, c] = parts;

  // Private ranges
  if (a === 10) return 'Private (RFC 1918)';
  if (a === 172 && b >= 16 && b <= 31) return 'Private (RFC 1918)';
  if (a === 192 && b === 168) return 'Private (RFC 1918)';

  // Special ranges
  if (a === 127) return 'Loopback';
  if (a === 169 && b === 254) return 'Link-Local (APIPA)';
  if (a >= 224 && a <= 239) return 'Multicast';
  if (a >= 240) return 'Reserved';
  if (a === 0) return 'Current Network';
  if (a === 100 && b >= 64 && b <= 127) return 'Carrier-Grade NAT (RFC 6598)';
  if (a === 192 && b === 0 && c === 0) return 'IETF Protocol Assignments';
  if (a === 192 && b === 0 && c === 2) return 'Documentation (TEST-NET-1)';
  if (a === 198 && b === 51 && c === 100) return 'Documentation (TEST-NET-2)';
  if (a === 203 && b === 0 && c === 113) return 'Documentation (TEST-NET-3)';
  if (a === 192 && b === 88 && c === 99) return '6to4 Relay Anycast';

  return 'Public';
}

// Calculate subnet info from CIDR
function calculateSubnet(cidr: string): SubnetInfo | null {
  const parts = cidr.split('/');
  if (parts.length !== 2) return null;

  const ip = parts[0];
  const prefix = parseInt(parts[1], 10);

  if (!isValidIp(ip) || isNaN(prefix) || prefix < 0 || prefix > 32) {
    return null;
  }

  const ipInt = ipToInt(ip);
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
  const wildcard = ~mask >>> 0;

  const networkInt = (ipInt & mask) >>> 0;
  const broadcastInt = (networkInt | wildcard) >>> 0;

  const totalHosts = Math.pow(2, 32 - prefix);
  const usableHosts = prefix >= 31 ? totalHosts : totalHosts - 2;

  const firstUsableInt = prefix >= 31 ? networkInt : networkInt + 1;
  const lastUsableInt = prefix >= 31 ? broadcastInt : broadcastInt - 1;

  const firstOctet = (networkInt >>> 24) & 255;

  return {
    cidr: `${intToIp(networkInt)}/${prefix}`,
    networkAddress: intToIp(networkInt),
    broadcastAddress: intToIp(broadcastInt),
    firstUsable: intToIp(firstUsableInt),
    lastUsable: intToIp(lastUsableInt),
    subnetMask: intToIp(mask),
    wildcardMask: intToIp(wildcard),
    totalHosts,
    usableHosts: Math.max(0, usableHosts),
    prefix,
    ipClass: getIpClass(firstOctet),
    ipType: getIpType(intToIp(networkInt)),
    binaryNetwork: ipToBinary(intToIp(networkInt)),
    binaryMask: ipToBinary(intToIp(mask)),
  };
}

// Common subnet presets
const COMMON_SUBNETS = [
  { prefix: 8, name: '/8 (Class A)', hosts: '16,777,214' },
  { prefix: 16, name: '/16 (Class B)', hosts: '65,534' },
  { prefix: 24, name: '/24 (Class C)', hosts: '254' },
  { prefix: 25, name: '/25', hosts: '126' },
  { prefix: 26, name: '/26', hosts: '62' },
  { prefix: 27, name: '/27', hosts: '30' },
  { prefix: 28, name: '/28', hosts: '14' },
  { prefix: 29, name: '/29', hosts: '6' },
  { prefix: 30, name: '/30 (P2P)', hosts: '2' },
  { prefix: 31, name: '/31 (P2P)', hosts: '2' },
  { prefix: 32, name: '/32 (Host)', hosts: '1' },
];

export function CIDRCalculator() {
  const [input, setInput] = useState('192.168.1.0/24');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const result = useMemo(() => {
    if (!input.trim()) return null;
    return calculateSubnet(input.trim());
  }, [input]);

  const handleCopy = useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const handlePresetClick = useCallback((prefix: number) => {
    const currentIp = input.split('/')[0];
    const ip = isValidIp(currentIp) ? currentIp : '192.168.1.0';
    setInput(`${ip}/${prefix}`);
  }, [input]);

  const CopyButton = ({ text, field }: { text: string; field: string }) => (
    <button
      onClick={() => handleCopy(text, field)}
      className="p-1 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
      title="Copy to clipboard"
    >
      {copiedField === field ? (
        <Check className="w-3.5 h-3.5 text-[var(--accent)]" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  );

  const renderOutput = () => {
    if (!input.trim()) {
      return (
        <div className="text-[var(--text-muted)] text-center py-8">
          Enter a CIDR notation (e.g., 192.168.1.0/24) to calculate subnet details.
        </div>
      );
    }

    if (!result) {
      return (
        <div className="text-red-400 p-4 bg-red-500/10 rounded-lg">
          <span className="font-semibold">Invalid Input:</span> Please enter a valid CIDR notation (e.g., 192.168.1.0/24)
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Summary Header */}
        <div className="flex items-center gap-4 p-4 rounded-lg bg-[var(--accent-dim)]">
          <Network className="w-10 h-10 text-[var(--accent)]" />
          <div className="flex-1">
            <div className="font-semibold text-lg text-[var(--accent)]">{result.cidr}</div>
            <div className="text-sm text-[var(--text-muted)]">
              {result.ipType} • Class {result.ipClass} • {result.usableHosts.toLocaleString()} usable hosts
            </div>
          </div>
        </div>

        {/* Address Details */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-3 flex items-center gap-2">
            <Network className="w-4 h-4" />
            Address Details
          </h3>
          <div className="space-y-2 text-sm bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
            <div className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
              <span className="text-[var(--text-muted)]">Network Address:</span>
              <span className="font-mono">{result.networkAddress}</span>
              <CopyButton text={result.networkAddress} field="network" />
            </div>
            <div className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
              <span className="text-[var(--text-muted)]">Broadcast Address:</span>
              <span className="font-mono">{result.broadcastAddress}</span>
              <CopyButton text={result.broadcastAddress} field="broadcast" />
            </div>
            <div className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
              <span className="text-[var(--text-muted)]">First Usable IP:</span>
              <span className="font-mono text-[var(--accent)]">{result.firstUsable}</span>
              <CopyButton text={result.firstUsable} field="first" />
            </div>
            <div className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
              <span className="text-[var(--text-muted)]">Last Usable IP:</span>
              <span className="font-mono text-[var(--accent)]">{result.lastUsable}</span>
              <CopyButton text={result.lastUsable} field="last" />
            </div>
            <div className="grid grid-cols-[140px_1fr_auto] gap-2 items-center pt-2 border-t border-[var(--border-color)]">
              <span className="text-[var(--text-muted)]">Total Addresses:</span>
              <span className="font-mono">{result.totalHosts.toLocaleString()}</span>
              <CopyButton text={String(result.totalHosts)} field="total" />
            </div>
            <div className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
              <span className="text-[var(--text-muted)]">Usable Hosts:</span>
              <span className="font-mono font-semibold text-[var(--accent)]">{result.usableHosts.toLocaleString()}</span>
              <CopyButton text={String(result.usableHosts)} field="usable" />
            </div>
          </div>
        </div>

        {/* Mask Details */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-3">Subnet Mask</h3>
          <div className="space-y-2 text-sm bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
            <div className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
              <span className="text-[var(--text-muted)]">CIDR Notation:</span>
              <span className="font-mono">/{result.prefix}</span>
              <CopyButton text={`/${result.prefix}`} field="cidr" />
            </div>
            <div className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
              <span className="text-[var(--text-muted)]">Subnet Mask:</span>
              <span className="font-mono">{result.subnetMask}</span>
              <CopyButton text={result.subnetMask} field="mask" />
            </div>
            <div className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
              <span className="text-[var(--text-muted)]">Wildcard Mask:</span>
              <span className="font-mono">{result.wildcardMask}</span>
              <CopyButton text={result.wildcardMask} field="wildcard" />
            </div>
          </div>
        </div>

        {/* Binary Representation */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-3">Binary Representation</h3>
          <div className="space-y-3 text-sm bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
            <div>
              <div className="text-[var(--text-muted)] text-xs mb-1">Network Address</div>
              <div className="font-mono text-xs break-all bg-[var(--bg-primary)] p-2 rounded border border-[var(--border-color)]">
                {result.binaryNetwork}
              </div>
            </div>
            <div>
              <div className="text-[var(--text-muted)] text-xs mb-1">Subnet Mask</div>
              <div className="font-mono text-xs break-all bg-[var(--bg-primary)] p-2 rounded border border-[var(--border-color)]">
                <span className="text-[var(--accent)]">{'1'.repeat(result.prefix)}</span>
                <span className="text-[var(--text-muted)]">{'0'.repeat(32 - result.prefix)}</span>
                <span className="text-[var(--text-muted)] ml-2">({result.prefix} network bits, {32 - result.prefix} host bits)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Visual Subnet Map */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-3">IP Range Visualization</h3>
          <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
            <div className="flex items-center gap-2 text-xs">
              <div className="flex-1 h-8 rounded-l bg-[var(--bg-tertiary)] flex items-center justify-center border border-[var(--border-color)] text-[var(--text-muted)]">
                Network
                <span className="ml-1 font-mono text-[var(--text-primary)]">{result.networkAddress}</span>
              </div>
              <div className="flex-[3] h-8 bg-[var(--accent-dim)] flex items-center justify-center border-y border-[var(--accent)]/30">
                <span className="text-[var(--accent)] font-medium">{result.usableHosts.toLocaleString()} Usable IPs</span>
              </div>
              <div className="flex-1 h-8 rounded-r bg-[var(--bg-tertiary)] flex items-center justify-center border border-[var(--border-color)] text-[var(--text-muted)]">
                Broadcast
                <span className="ml-1 font-mono text-[var(--text-primary)]">{result.broadcastAddress}</span>
              </div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-[var(--text-muted)]">
              <span>↑ Reserved</span>
              <span>{result.firstUsable} — {result.lastUsable}</span>
              <span>Reserved ↑</span>
            </div>
          </div>
        </div>

        {/* Quick Reference */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-3">Quick Subnet Reference</h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {COMMON_SUBNETS.map((subnet) => (
              <button
                key={subnet.prefix}
                onClick={() => handlePresetClick(subnet.prefix)}
                className={`p-2 rounded text-xs border transition-colors ${
                  result.prefix === subnet.prefix
                    ? 'bg-[var(--accent-dim)] border-[var(--accent)] text-[var(--accent)]'
                    : 'bg-[var(--bg-secondary)] border-[var(--border-color)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--text-primary)]'
                }`}
              >
                <div className="font-mono font-semibold">/{subnet.prefix}</div>
                <div className="text-[0.65rem] opacity-75">{subnet.hosts}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-[var(--text-primary)]">
          CIDR Notation
        </label>
        <div className="flex gap-2 sm:gap-3">
          <input
            type="text"
            className="flex-1 min-w-0 px-3 sm:px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-sm"
            placeholder="192.168.1.0/24"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck={false}
          />
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          Enter IP address with CIDR prefix (e.g., 10.0.0.0/8, 172.16.0.0/12, 192.168.1.0/24)
        </p>
      </div>

      {/* Output Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--text-primary)]">
          Subnet Information
        </label>
        <div className="output-panel">
          {renderOutput()}
        </div>
      </div>
    </div>
  );
}
